import { gql } from '@apollo/client';

export const fragments = {
  projectConfigItem: gql`
    fragment ProjectConfigItem on ProjectConfig {
      id
      weekStartDay
      weekEndDay
      weekStartTime
      weekEndTime
      usersQuantityDisplayToggle
      allowApprovalByNotification
    }
  `,
  weldConfigItem: gql`
    fragment WeldConfigItem on ProjectConfig {
      id
      weldConfig {
        fitUpWorkflowEnabled
        weldOrganization
      }
    }
  `,
  facilityUnitRates: gql`
    fragment FacilityUnitRates on ProjectConfig {
      id
      facilityUnitRates {
        facility {
          id
          name
        }
      }
    }
  `,

  projectLocationObject: gql`
    fragment ProjectLocationObject on ProjectConfig {
      id
      projectLocation {
        minLatitude
        minLongitude
        maxLatitude
        maxLongitude
      }
    }
  `,
};

export const GET_PROJECTS = gql`
  query projects {
    projects {
      id
      name
      jobNumber
      subJobNumber
      location
      projectConfiguration {
        id
        totalWeeks
        projectWeeks {
          weekNumber
          startDate
          isCurrent
          endDate
          id
        }
      ...ProjectLocationObject
      }

      state {
        id
        name
        countryId
      }
      country {
        id
        name
        locale
      }
      gbu {
        id
        description
      }
      logoAttachment {
        id
        mimeType
        fileName
        fileSize
        id
        url
        creator {
          id
          lastName
        }
      }
    }
    ${fragments.projectLocationObject}
  }
`;

export const GET_PROJECT_BY_IDS = gql`
  query projectsByIds($ids: [ID!]!) {
    projectsByIds(ids: $ids) {
      id
      name
      jobNumber
      state {
        id
        name
      }
      country {
        id
        name
      }
      location
      logoAttachment {
        id
        attachmentType
        mimeType
        url
        fileName
        fileSize
      }

      language {
        id
        name
      }
      applicationSupportInfo
      projectConfiguration {
        id
        projectTimeZone {
          id
          displayText
        }
        ...ProjectLocationObject
        projectSupportUsers {
          id
          user {
            id
            firstName
            lastName
            crewMember {
              id
              imageUrl
              persona
            }
          }
          email
          phone
          location
        }
        appDataFormats {
          id
          formatTypeId
          format
          description
        }
        isoDocumentPrefixList
      }
    }
    ${fragments.projectLocationObject}
  }
`;

export const GET_PROJECT_DATA_BY_ID = gql`
  query projectsByIds($ids: [ID!]!) {
    projectsByIds(ids: $ids) {
      id
      projectConfiguration {
        id
        appDataFormats {
          id
          formatTypeId
          format
        }
      }
    }
  }
`;

export const GET_PROJECT_COMMODITY_TYPES_BY_ID = gql`
  query projectsByIds($ids: [ID!]!) {
    projectsByIds(ids: $ids) {
      id
      projectConfiguration {
        id
        projectCommodityTypes {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation updateProject($input: UpdateProjectInput!) {
    updateProject(input: $input) {
      project {
        id
        name
        state {
          id
        }
        country {
          id
        }
        location
        logoAttachment {
          id
          attachmentType
          mimeType
          url
          fileName
          fileSize
        }
        timeZone
        projectTimeZone {
          id
          displayText
        }
        language {
          id
        }
        projectConfiguration {
          id
        ...ProjectLocationObject
         isoDocumentPrefixList
        }
        applicationSupportInfo
        projectSupportUsers {
          id
          email
          phone
          location
        }
        appDataFormats {
          id
          formatTypeId
          format
        }
      }
      error
    }
    ${fragments.projectLocationObject}
  }
`;

export const UPDATE_USER_PROJECT = gql`
  mutation setUserDefaultProject($input: SetUserDefaultProjectInput!) {
    setUserDefaultProject(input: $input) {
      user {
        id
        project {
          id
          weekStartDate
        }
      }
      error
    }
  }
`;

export const GET_LANGUAGES = gql`
  query languages {
    languages {
      id
      name
    }
  }
`;

export const GET_COUNTRIES = gql`
  query countries {
    countries {
      id
      name
      locale
    }
  }
`;

export const GET_STATES = gql`
  query states($countryId: String!) {
    states(countryId: $countryId) {
      id
      name
    }
  }
`;

export const GET_APP_DATA_FORMATS = gql`
  query appDataFormats($formatTypeId: AppDataFormatEnum!) {
    appDataFormats(formatTypeId: $formatTypeId) {
      id
      formatTypeId
      format
      description
    }
  }
`;

export const GET_TIMEZONES = gql`
  query timeZones {
    timeZones {
      id
      offset
      abbreviation
      displayText
      isDst
    }
  }
`;

export const GET_PROJECT_CREW_TYPES = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      crewTypes {
        id
        name
        description
        applicableToProject
      }
    }
  }
`;
export const SAVE_CREW_TYPE_SELECTION = gql`
  mutation saveCrewTypeSelection($input: [ID!]!) {
    saveCrewTypeSelection(input: $input) {
      crewTypes {
        id
        applicableToProject
      }
      error
    }
  }
`;

export const GET_PROJECT_CONFIG_BY_ID_WITH_DISCIPLINES = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      ...ProjectConfigItem
      workPackageDisciplines {
       id
      name
      isActive
      allowAnySequenceClaim
    }
    }
     ${fragments.projectConfigItem}
  }
`;

export const GET_PROJECT_WORK_PACKAGE_DISCIPLINES = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      workPackageDisciplines {
        id
        name
      }
    }
  }
`;

export const GET_PROJECT_WORK_PACKAGE_DISCIPLINES_WITH_STATUS = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      workPackageDisciplines {
        id
        name
        isActive
      }
    }
  }
`;

export const GET_PROJECT_CONFIG_PLAN_TARGET_CALC = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      compositeUnitRate {
        unitRate
        uom {
          id
          uomTypeId
        }
      }
      facilityUnitRates {
        unitRate {
          unitRate
          uom {
            id
            uomTypeId
          }
        }
        facility {
          id
        }
      }
      indirectTax
      projectAdjustmentFactor
    }
  }
`;

export const GET_PROJECT_CONFIG_MODULES = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      modules {
        id
        name
        moduleTypeId
        isActive
      }
    }
  }
`;

export const GET_WELD_PROGRAM_MGMT_CONFIG = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      ...WeldConfigItem
    }
    ${fragments.weldConfigItem}
  }
`;

export const GET_PROJECT_FACILITIES = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      ...FacilityUnitRates
    }
    ${fragments.facilityUnitRates}
  }
`;

export const GET_AVAILABLE_MILESTONE_TYPES = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      availableMilestoneTypes {
        id
        name
      }
    }
  }
`;

export const GET_PROJECT_WEEKS = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      projectWeeks {
        id
        weekNumber
        year
        isCurrent
      }
    }
  }
`;

export const GET_PROJECT_COMMODITY_TYPES = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      projectCommodityTypes {
        id
        name
      }
    }
  }
`;

export const SAVE_QTY_REPORTING_CONFIG = gql`
  mutation saveQuantityReportingConfig($input: SaveQuantityReportingConfigInput!) {
    saveQuantityReportingConfig(input: $input) {
      projectConfig {
        ...ProjectConfigItem
      }
      workPackageDisciplines {
        id
        allowAnySequenceClaim
      }
      error
    }
    ${fragments.projectConfigItem}
  }
`;

export const SAVE_PROJECT_CONFIG_PLAN_TARGET_CALC = gql`
  mutation savePlanningAndTargetConfig($input: SavePlanningAndTargetConfigInput!) {
    savePlanningAndTargetConfig(input: $input) {
      projectConfig {
        id
        compositeUnitRate {
          unitRate
        }
        facilityUnitRates {
          unitRate {
            unitRate
          }
          facility {
            id
          }
        }
        indirectTax
        projectAdjustmentFactor
      }
      error
    }
  }
`;

export const UPDATE_PROJECT_MODULES = gql`
  mutation updateModules($input: [UpdateModuleInput]!) {
    updateModules(input: $input) {
      modules {
        id
        isActive
      }
      error
    }
  }
`;

export const SAVE_WELD_PROGRAM_CONFIG = gql`
  mutation saveWeldProgramConfig($input: SaveWeldProgramConfigInput!) {
    saveWeldProgramConfig(input: $input) {
      projectConfig {
        ...WeldConfigItem
      }
      error
    }
    ${fragments.weldConfigItem}
  }
`;

export const GET_PROJECT_ITR_CONFIG = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      projectItrConfig {
        id
        name
        itrType
        projectCommodityTypeItrs {
          id
          projectCommodityType {
            id
            name
            projectMilestoneTypes {
              id
              name
            }
          }
          projectMilestoneType {
            id
            name
          }
        }
      }
    }
  }
`;

export const SAVE_ITR_CONFIG = gql`
  mutation saveItrConfig($input: [SaveItrConfigInput]!) {
    saveItrConfig(input: $input) {
      projectItrConfig {
        id
        itrType
        projectCommodityTypeItrs {
          id
          projectMilestoneType {
            id
          }
        }
      }
      error
    }
  }
`;
export const ADD_IMPORT_SCHEDULE = gql`
  mutation addImportSchedule($input: AddImportScheduleInput!) {
    addImportSchedule(input: $input) {
      importSchedule {
        id
        startDatetime
        importType
        importStatus {
          status
        }
      }
      error
    }
  }
`;
export const GET_IMPORT_SCHEDULE = gql`
  query importSchedules {
    importSchedules {
      id
      startDatetime
      endDatetime
      importType
      importStatus {
        status
      }
      importSummary {
        totalWorkPackages
        totalCommodities
        totalMilestones
        totalWorkPackageOwners
        totalUsers
      }
      errorSummary {
        totalWorkPackages
        totalCommodities
        totalMilestones
        totalWorkPackageOwners
        totalUsers
      }
    }
  }
`;

export const GET_EVENT_NOTIFICATIONS = gql`
  query eventNotifications($input: EventNotificationInput!, $paging: PagingInput!) {
    eventNotifications(input: $input, paging: $paging) {
      cursor
      total
      items {
        id
        identifier
        status
        errorTypeId
        entityTypeId
        notificationTypeId
        error
        createdAt
        retryCount
        nextScheduleAt
      }
    }
  }
`;

export const GET_EVENT_NOTIFICATION_STATS = gql`
  query eventNotificationStats($input: EventNotificationInput!) {
    eventNotificationStats(input: $input) {
      stats
      entityType
    }
  }
`;

export const SYNC_EVENT_NOTIFICATIONS = gql`
  mutation syncEventNotifications($input: SyncEventNotificationsInput!) {
    syncEventNotifications(input: $input) {
      errors {
        error
        id
      }
    }
  }
`;

export const GET_PROJECT_CONFIG_FEATURES = gql`
  query projectFeatures($id: ID!) {
    projectConfigById(id: $id) {
      id
      features {
        enabled
        id
        metaData
        readOnly
        featureDefinition {
          description
          id
          name
          metaDataDefinition
        }
      }
    }
  }
`;

export const SAVE_PROJECT_FEATURES = gql`
  mutation saveFeatures($input: [SaveFeatureInput!]!) {
    saveFeatures(input: $input) {
      error
      features {
        enabled
        id
        metaData
        featureDefinition {
          id
          description
        }
      }
    }
  }
`;

export const SUBSCRIPTION_EVENT_NOTIFICATION_UPDATED = gql`
  subscription notificationUpdatated {
    notificationUpdated {
      id
    }
  }
`;

export const GET_EVENT_NOTIFICATIONS_BY_IDS = gql`
  query eventNotificationsByIds($ids: [ID!]!) {
    eventNotificationsByIds(ids: $ids) {
      id
      identifier
      status
      errorTypeId
      entityTypeId
      notificationTypeId
      error
      createdAt
      retryCount
      nextScheduleAt
    }
  }
`;

export const GET_PROJECT_MILESTONE_LIST = gql`
  query projectConfigById($id: ID!) {
    projectConfigById(id: $id) {
      id
      projectCommodityTypes {
        id
        lobCommodityTypeId
        projectMilestoneTypes {
          id
          lobMilestoneTypeId
        }
      }
    }
  }
`;
